import React, { Fragment, useState } from 'react';
import { all, append, contains, filter, isEmpty, map, without } from 'ramda';
import PropTypes from 'prop-types';
import LeagueSidePropTypes from 'utils/LeagueSidePropTypes';
import { formatToDollar } from 'utils/formatNumber';
import { pluralizedTense } from 'utils/string';
import { pluckId } from 'utils/misc';
import {
  isRejectedByLeague,
  offerStatus,
  sortOffersByOfferCol,
} from 'utils/offer';
import ActiveOffersBadges from 'components/ActiveOffersBadges';
import Badge from 'components/Badge';
import SendOfferEmailsModal from 'components/SendOfferEmailsModal';
import RemoveFromCampaignModal from 'components/RemoveFromCampaignModal';
import RecentCommentRow from './RecentCommentRow';
import SponsorablePropertyCol from './SponsorablePropertyCol';
import OrganizationCol from './OrganizationCol';

const responseCol = (offer) => {
  if (isRejectedByLeague(offer)) {
    return <Badge badgeType="warning">Rejected By Organization</Badge>;
  }

  const { numberOfOfferViews } = offer;

  return (
    !!numberOfOfferViews && (
      <Badge>{`${numberOfOfferViews} ${pluralizedTense(
        numberOfOfferViews,
        'View',
      )}`}</Badge>
    )
  );
};

const ProposedTable = ({ offers, onRemoveFromCampaign, onSendOfferEmails }) => {
  const [selectedOfferIds, setSelectedOfferIds] = useState([]);

  const allOfferIds = pluckId(offers);
  const selectedIdsSet = new Set(selectedOfferIds);
  const allSelected = all((id) => selectedIdsSet.has(id), allOfferIds);
  const selectedOffers = filter(
    ({ id }) => contains(id, selectedOfferIds),
    offers,
  );

  const removeOfferIdAndRefetch = () => {
    setSelectedOfferIds([]);
    onRemoveFromCampaign();
  };

  const handleToggle = (offerId) => {
    const includesOfferId = contains(offerId, selectedOfferIds);
    const removeOfferId = without([offerId], selectedOfferIds);
    const addOfferId = append(offerId, selectedOfferIds);

    return () =>
      setSelectedOfferIds(() => (includesOfferId ? removeOfferId : addOfferId));
  };

  const handleToggleAll = () => {
    setSelectedOfferIds(allSelected ? [] : allOfferIds);
  };

  const columnHeaders = [
    <th scope="col" className="align-middle" key="checkbox">
      <input
        checked={allSelected}
        data-test="all-proposed-toggle"
        onChange={handleToggleAll}
        type="checkbox"
      />
    </th>,
    <th scope="col" className="align-middle pr-1" key="organization">
      Organization
    </th>,
    <th scope="col" className="align-middle pr-1" key="sponsorable-property">
      Sponsorable Property
    </th>,
    <th
      scope="col"
      className="align-middle pl-1"
      key="report-column"
      aria-label="report-column"
    />,
    <th scope="col" className="align-middle" key="value">
      Value
    </th>,
    <th scope="col" className="align-middle" key="offer">
      Offer
    </th>,
    <th scope="col" className="align-middle" key="response">
      Response
    </th>,
  ];

  const renderRow = (offer) => {
    const {
      id: offerId,
      offerCountsReport,
      sponsorableProperty,
      recentComment,
      fundraiserConflict,
      surveyConflict,
    } = offer;
    const { organization } = sponsorableProperty;
    return (
      <Fragment key={offerId}>
        <tr data-test={`offer-${offerId}-row`} className="p-2">
          <td className="align-middle">
            <input
              checked={contains(offerId, selectedOfferIds)}
              data-test={`proposed-toggle-${offerId}`}
              onChange={handleToggle(offerId)}
              type="checkbox"
            />
          </td>
          <td className="align-middle" data-test="organization-col">
            <OrganizationCol organization={organization} />
          </td>
          <SponsorablePropertyCol
            sponsorableProperty={sponsorableProperty}
            offerId={offerId}
          />
          <td className="align-middle pl-1">
            <ActiveOffersBadges
              offerCountsReport={offerCountsReport}
              sponsorablePropertyId={sponsorableProperty.id}
              fundraiserConflict={fundraiserConflict}
              surveyConflict={surveyConflict}
            />
          </td>
          <td className="align-middle" data-test="value">
            <small>{offer.value ? formatToDollar(offer.value) : '--'}</small>
          </td>
          <td className="align-middle" data-test="offer-col">
            <small>{offerStatus(offer)}</small>
          </td>
          <td className="align-middle" data-test="response">
            {responseCol(offer)}
          </td>
        </tr>
        {!!recentComment && (
          <RecentCommentRow colLength={columnHeaders.length} offer={offer} />
        )}
      </Fragment>
    );
  };

  return (
    <>
      <div
        className="d-flex mb-2 justify-content-between align-items-center"
        data-test="proposed-table"
      >
        <p className="m-0 sui-font-bold">{`${offers.length} Proposed`}</p>
        <span>
          <div className="dropdown d-inline-block ml-2">
            <button
              className="btn btn-large btn-secondary dropdown-toggle"
              disabled={isEmpty(selectedOffers)}
              data-toggle="dropdown"
              id="bulk-proposed-dropdown-menu-button"
              type="button"
            >
              {`With ${
                selectedOffers.length
              } Selected Sponsorable ${pluralizedTense(
                selectedOffers.length,
                'Property',
              )}`}
            </button>
            <div
              className="dropdown-menu"
              aria-labelledby="bulk-proposed-dropdown-menu-button"
            >
              {!!selectedOffers.length && (
                <SendOfferEmailsModal
                  id="bulk-proposed-offer-email-modal"
                  offers={selectedOffers}
                  onSend={onSendOfferEmails}
                  toggleClassName="btn btn-link dropdown-item"
                />
              )}
              <RemoveFromCampaignModal
                offers={selectedOffers}
                onRemove={removeOfferIdAndRefetch}
                prefix="bulk-proposed"
              />
            </div>
          </div>
        </span>
      </div>
      {offers.length ? (
        <table className="table" data-test="proposed-table-offers">
          <thead>
            <tr>{map((header) => header, columnHeaders)}</tr>
          </thead>
          <tbody>{map(renderRow, sortOffersByOfferCol(offers))}</tbody>
        </table>
      ) : (
        <p className="text-center">No proposed sponsorships exist!</p>
      )}
    </>
  );
};

ProposedTable.propTypes = {
  offers: PropTypes.arrayOf(LeagueSidePropTypes.offer).isRequired,
  onRemoveFromCampaign: PropTypes.func.isRequired,
  onSendOfferEmails: PropTypes.func.isRequired,
};

export default ProposedTable;
